import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { networkManager, getErrorMessage } from '../api/NetworkManager';
import { ArrowLeft, Loader2, Send, Check, ChevronLeft, ChevronRight, Radio } from 'lucide-react';

interface LiveSession {
  sessionId: string;
  status: 'active' | 'paused' | 'ended';
  currentTeamId: string | null;
  currentTeamIndex: number;
  teamOrder: Array<{
    teamId: string;
    teamName: string;
    themeName?: string;
    status: 'waiting' | 'presenting' | 'completed';
  }>;
  timerState: 'running' | 'paused' | 'stopped' | 'expired';
  timerRemainingSeconds: number;
  timerDurationSeconds: number;
}

interface Submission {
  submissionId: string;
  teamId: string;
  title: string;
  problem: string;
  solution: string;
  impact: string;
  technicalApproach: string;
  demoUrl?: string;
  repoUrl?: string;
  fileUrls: string[];
  status: string;
}

interface ScoringCriterion {
  name: string;
  weight: number;
  maxScore: number;
}

interface EventDetail {
  eventId: string;
  name: string;
  scoringCriteria: ScoringCriterion[];
}

function formatTime(seconds: number): string {
  const clamped = Math.max(0, seconds);
  const m = Math.floor(clamped / 60);
  const s = clamped % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function timerColorClass(seconds: number, timerState: string): string {
  if (timerState === 'expired' || seconds <= 0) return 'text-red-500';
  if (seconds <= 30) return 'text-yellow-500';
  return 'text-green-500';
}

export function LiveJudgeViewPage() {
  const { eventId } = useParams();
  
  const [session, setSession] = useState<LiveSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState<EventDetail | null>(null);
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [scores, setScores] = useState<Record<string, number>>({});
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [scoreError, setScoreError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [localRemaining, setLocalRemaining] = useState(0);
  const [manualTeamId, setManualTeamId] = useState<string | null>(null); // null = synced to live
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastTeamIdRef = useRef<string | null>(null);

  // Fetch live session
  const fetchSession = useCallback(async () => {
    if (!eventId) return;
    try {
      const res = await networkManager.get<{ status: boolean; data: LiveSession }>(
        `/api/events/${eventId}/live`,
      );
      setSession(res.data);
      setLocalRemaining(res.data.timerRemainingSeconds);
    } catch {
      // Session not available
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  // Load event for scoring criteria
  useEffect(() => {
    if (!eventId) return;
    networkManager
      .get<{ status: boolean; data: EventDetail }>(`/api/events/${eventId}`)
      .then((res) => setEvent(res.data))
      .catch(console.error);
  }, [eventId]);

  // Initial load
  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  // Poll
  useEffect(() => {
    if (!session || session.status === 'ended') return;
    pollRef.current = setInterval(fetchSession, 2000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [session?.status, fetchSession]);

  // Local countdown
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (!session || session.timerState !== 'running') return;

    timerRef.current = setInterval(() => {
      setLocalRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [session?.timerState, session?.timerRemainingSeconds]);

  // The team the judge is currently viewing
  const activeTeamId = manualTeamId ?? session?.currentTeamId ?? null;
  const isSynced = manualTeamId === null;

  // When activeTeamId changes, load their submission
  useEffect(() => {
    if (!activeTeamId || !eventId) return;
    if (activeTeamId === lastTeamIdRef.current) return;

    lastTeamIdRef.current = activeTeamId;
    setSubmission(null);
    setSubmitted(false);
    setComment('');

    // Reset scores
    if (event?.scoringCriteria) {
      const initial: Record<string, number> = {};
      event.scoringCriteria.forEach((c) => {
        initial[c.name] = 0;
      });
      setScores(initial);
    }

    // Load submission and existing scores
    networkManager
      .get<{ status: boolean; data: Submission }>(
        `/api/events/${eventId}/teams/${activeTeamId}/submissions`,
      )
      .then((res) => setSubmission(res.data))
      .catch(console.error);

    // Check if judge already scored this team
    networkManager
      .get<{ status: boolean; data: Array<{ criteria: Array<{ name: string; score: number; comment?: string }> }> }>(
        `/api/events/${eventId}/scores/teams/${activeTeamId}`,
      )
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length > 0) {
          const existing = res.data[0];
          const loaded: Record<string, number> = {};
          existing.criteria.forEach((c) => { loaded[c.name] = c.score; });
          setScores(loaded);
          setComment(existing.criteria.map((c) => c.comment).filter(Boolean).join('; '));
          setSubmitted(true);
        }
      })
      .catch(() => {});
  }, [activeTeamId, eventId, event?.scoringCriteria]);

  // Weighted total
  const weightedTotal = useMemo(() => {
    if (!event) return 0;
    return event.scoringCriteria.reduce((sum, c) => {
      const score = scores[c.name] ?? 0;
      const normalized = c.maxScore > 0 ? score / c.maxScore : 0;
      return sum + normalized * c.weight;
    }, 0);
  }, [event, scores]);

  // Submit scores
  async function handleSubmitScores() {
    if (!eventId || !activeTeamId) return;
    setSubmitting(true);
    setScoreError(null);
    try {
      await networkManager.post(`/api/events/${eventId}/scores`, {
        teamId: activeTeamId,
        criteria: Object.entries(scores).map(([name, score]) => ({
          name,
          score,
          comment: comment.trim() || undefined,
        })),
      });
      setSubmitted(true);
    } catch (err) {
      setScoreError(getErrorMessage(err, 'Failed to submit scores'));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-ink-tertiary" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="animate-fade-in">
        <Link to={`/events/${eventId}`} className="btn-ghost mb-4 inline-flex">
          <ArrowLeft className="h-4 w-4" /> Back to Event
        </Link>
        <p className="py-12 text-center text-ink-secondary">
          No live session is currently active.
        </p>
      </div>
    );
  }

  const currentTeam = session.teamOrder.find((t) => t.status === 'presenting');
  const activeTeamEntry = session.teamOrder.find((t) => t.teamId === activeTeamId);
  const activeIdx = session.teamOrder.findIndex((t) => t.teamId === activeTeamId);
  const prevTeamEntry = activeIdx > 0 ? session.teamOrder[activeIdx - 1] : null;
  const nextTeamEntry = activeIdx >= 0 && activeIdx < session.teamOrder.length - 1 ? session.teamOrder[activeIdx + 1] : null;

  function navigateToTeam(teamId: string) {
    lastTeamIdRef.current = null; // force reload
    setManualTeamId(teamId);
  }

  function syncToLive() {
    lastTeamIdRef.current = null;
    setManualTeamId(null);
  }

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between">
        <Link to={`/events/${eventId}`} className="btn-ghost inline-flex">
          <ArrowLeft className="h-4 w-4" /> Back to Event
        </Link>
        {!isSynced && (
          <button onClick={syncToLive} className="btn-primary text-sm inline-flex items-center gap-1.5">
            <Radio className="h-4 w-4" /> Sync to Presenting Team
          </button>
        )}
      </div>

      <h1 className="mt-2 font-display text-2xl font-bold text-ink">Judge View</h1>

      {/* Timer + Current Team Bar */}
      <div className="mt-4 card p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-ink-tertiary">
              {isSynced ? 'Now Presenting' : 'Viewing (not synced)'}
            </p>
            <h2 className="mt-0.5 font-display text-xl font-bold text-ink">
              {activeTeamEntry?.teamName ?? currentTeam?.teamName ?? 'Waiting...'}
            </h2>
            {activeTeamEntry?.themeName && (
              <p className="text-sm text-ink-secondary">{activeTeamEntry.themeName}</p>
            )}
          </div>
          <div className="text-right">
            <p
              className={`font-mono text-4xl font-bold ${timerColorClass(localRemaining, session.timerState)}`}
            >
              {formatTime(localRemaining)}
            </p>
            <p className="mt-0.5 text-xs text-ink-tertiary">
              Team {(activeIdx >= 0 ? activeIdx : session.currentTeamIndex) + 1} of {session.teamOrder.length}
            </p>
          </div>
        </div>
        {/* Prev / Next navigation */}
        <div className="mt-3 flex items-center justify-between border-t border-ring pt-3">
          <button
            className="btn-ghost text-sm inline-flex items-center gap-1"
            onClick={() => prevTeamEntry && navigateToTeam(prevTeamEntry.teamId)}
            disabled={!prevTeamEntry}
          >
            <ChevronLeft className="h-4 w-4" /> Previous
          </button>
          <div className="flex gap-1 overflow-x-auto">
            {session.teamOrder.map((t) => (
              <button
                key={t.teamId}
                onClick={() => navigateToTeam(t.teamId)}
                title={t.teamName}
                className={`h-2.5 w-2.5 rounded-full transition-colors ${
                  t.teamId === activeTeamId
                    ? 'bg-accent'
                    : t.status === 'completed'
                      ? 'bg-green-400'
                      : t.status === 'presenting'
                        ? 'bg-yellow-400'
                        : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
          <button
            className="btn-ghost text-sm inline-flex items-center gap-1"
            onClick={() => nextTeamEntry && navigateToTeam(nextTeamEntry.teamId)}
            disabled={!nextTeamEntry}
          >
            Next <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Submission Details */}
      {submission && (
        <div className="mt-4 space-y-3">
          <div className="card p-4">
            <h3 className="font-semibold text-ink">{submission.title || 'Untitled'}</h3>
            <div className="mt-2 flex flex-wrap gap-3">
              {submission.demoUrl && (
                <a href={submission.demoUrl} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-purple-600 hover:underline">Demo Link</a>
              )}
              {submission.repoUrl && (
                <a href={submission.repoUrl} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-purple-600 hover:underline">Repository</a>
              )}
            </div>
          </div>
          {[
            { key: 'problem', label: 'Problem Statement' },
            { key: 'solution', label: 'Solution' },
            { key: 'impact', label: 'Impact' },
            { key: 'technicalApproach', label: 'Technical Approach' },
          ].map((section) => {
            const content = (submission as unknown as Record<string, string>)[section.key];
            return (
              <div key={section.key} className="card p-4">
                <h4 className="text-sm font-semibold text-ink">{section.label}</h4>
                <p className="mt-1 whitespace-pre-wrap text-sm text-ink-secondary">
                  {content?.trim() || <span className="italic text-ink-tertiary">No content</span>}
                </p>
              </div>
            );
          })}

          {/* Attachments */}
          {submission.fileUrls && submission.fileUrls.length > 0 && (
            <div className="card p-4">
              <h4 className="text-sm font-semibold text-ink">Attachments</h4>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {submission.fileUrls.map((url, i) => {
                  const isImage = /\.(png|jpg|jpeg|gif|webp)$/i.test(url);
                  return isImage ? (
                    <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block">
                      <img src={url} alt={`Attachment ${i + 1}`} className="w-full rounded-lg border border-gray-200 object-cover" />
                    </a>
                  ) : (
                    <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-lg border border-gray-200 p-3 text-sm text-ink hover:bg-surface-sunken">
                      {decodeURIComponent(url.split('/').pop() ?? `File ${i + 1}`)}
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Scoring Form */}
      {activeTeamId && event?.scoringCriteria && (
        <div className="mt-4 card p-4">
          <h3 className="font-semibold text-ink">Score</h3>

          {submitted ? (
            <div className="mt-3 flex items-center justify-between rounded-lg bg-green-50 p-4">
              <span className="flex items-center gap-2 font-medium text-green-700">
                <Check className="h-4 w-4" /> Scores submitted for this team.
              </span>
              <button className="text-xs font-medium text-green-800 underline" onClick={() => setSubmitted(false)}>Edit</button>
            </div>
          ) : (
            <>
              <div className="mt-3 space-y-3">
                {event.scoringCriteria.map((criterion) => (
                  <div key={criterion.name}>
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-ink">
                        {criterion.name}
                        <span className="ml-1 text-xs text-ink-tertiary">
                          (max {criterion.maxScore}, weight {criterion.weight}%)
                        </span>
                      </label>
                      <span className="font-mono text-sm font-semibold text-ink">
                        {scores[criterion.name] ?? 0}
                      </span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={criterion.maxScore}
                      step={1}
                      value={scores[criterion.name] ?? 0}
                      onChange={(e) =>
                        setScores((prev) => ({
                          ...prev,
                          [criterion.name]: Number(e.target.value),
                        }))
                      }
                      className="mt-1 w-full"
                    />
                  </div>
                ))}
              </div>

              {/* Weighted total */}
              <div className="mt-4 flex items-center justify-between rounded-lg bg-gray-50 p-3">
                <span className="text-sm font-semibold text-ink">Weighted Total</span>
                <span className="text-lg font-bold text-brand">
                  {weightedTotal.toFixed(1)} / 100
                </span>
              </div>

              <div className="mt-4">
                <label className="text-sm font-medium text-ink">Notes (optional)</label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={2}
                  className="mt-1 w-full rounded-lg border border-ring bg-surface px-3 py-2 text-sm text-ink"
                  placeholder="Feedback for this team..."
                />
              </div>

              <button
                className="btn-primary mt-4 inline-flex items-center gap-2"
                onClick={handleSubmitScores}
                disabled={submitting}
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Submit Scores
              </button>
              {scoreError && (
                <p className="mt-2 text-sm text-red-600">{scoreError}</p>
              )}
            </>
          )}
        </div>
      )}

      {/* Team Queue */}
      <div className="mt-4 card p-4">
        <h3 className="text-sm font-semibold text-ink">Team Queue</h3>
        <div className="mt-2 space-y-1">
          {session.teamOrder.map((t) => (
            <button
              key={t.teamId}
              onClick={() => navigateToTeam(t.teamId)}
              className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                t.teamId === activeTeamId
                  ? 'bg-accent/10 border border-accent'
                  : 'hover:bg-surface-sunken'
              }`}
            >
              <span className={`font-medium ${t.teamId === activeTeamId ? 'text-accent' : 'text-ink'}`}>
                {t.teamName}
              </span>
              <span className={`text-xs rounded-full px-2 py-0.5 ${
                t.status === 'completed' ? 'bg-green-100 text-green-700' :
                t.status === 'presenting' ? 'bg-yellow-100 text-yellow-700' :
                'bg-gray-100 text-gray-600'
              }`}>
                {t.status}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
